import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CloseIcon } from "@/assets/icons";
import { Accordion } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RootState } from "@/store";
import { saveWeightValues } from "@/store/slice/datasetSlice";
import { setSpatialConfig } from "@/store/slice/spatialSlice";
import { WeightValueEntry, WeightValues } from "@/types/dataset";
import { ScoringOptions, ScoringOptionsEnum } from "@/types/spatial";
import { areObjectsDifferentDeeply } from "@/utils/common";
import { getAvailableDatasets } from "@/utils/dataset";
import { hasNonZeroValue } from "@/utils/weightValues";

import { twMerge } from "tailwind-merge";
import { useDeepCompareMemo } from "use-deep-compare";

import DatasetAttributeItem from "./DatasetAttributeItem";
import ScoringOptionsSection from "./ScoringOptionsSection";
import {
  calculateAttributeGlobalPercentages,
  calculateDatasetWeightPercentages,
  initializeDatasetWeights
} from "./utils";
import WeightSummarySection from "./WeightSummarySection";

interface Props {
  onClose: () => void;
}

const DataWeightSettingsPopupV2 = ({ onClose }: Props) => {
  const dispatch = useDispatch();
  const { datasets, weightValues, visualizationOrder } = useSelector(
    (state: RootState) => state.dataset
  );
  const { spatialConfig } = useSelector((state: RootState) => state.spatial);
  const { scoringOption: scoringOptionState } = spatialConfig;

  const availableDatasets = getAvailableDatasets(datasets);

  const filteredVisualizationOrder = visualizationOrder;

  const totalAttributes = filteredVisualizationOrder.reduce(
    (sum, datasetId) =>
      sum + Object.keys(weightValues[datasetId]?.attributes ?? {}).length,
    0
  );

  const [localWeightValues, setLocalWeightValues] =
    useState<WeightValues>(weightValues);
  const [selectedScoringOption, setSelectedScoringOption] =
    useState<ScoringOptions | null>(
      scoringOptionState || ScoringOptionsEnum.NORMALIZED
    );

  const datasetWeightPercentages =
    calculateDatasetWeightPercentages(localWeightValues);

  const allAttributeGlobalPercentages = useDeepCompareMemo(
    () =>
      Object.fromEntries(
        filteredVisualizationOrder.map((datasetId) => [
          datasetId,
          calculateAttributeGlobalPercentages(
            Object.fromEntries(
              Object.entries(
                localWeightValues[datasetId]?.attributes || {}
              ).filter(([, value]) => value !== undefined)
            ) as Record<string, WeightValueEntry>,
            datasetWeightPercentages[datasetId]
          )
        ])
      ),
    [localWeightValues, datasetWeightPercentages, filteredVisualizationOrder]
  );

  const getCombinedWeightValues = (): WeightValues => {
    return Object.fromEntries(
      Object.keys(localWeightValues).map((datasetId) => {
        const dataset = localWeightValues[datasetId];
        const combinedAttributes = Object.fromEntries(
          Object.entries(dataset.attributes)
            .filter(([, entry]) => entry !== undefined)
            .map(([attributeId, entry]) => {
              const baseEntry: WeightValueEntry =
                typeof entry === "object" && entry !== undefined
                  ? (entry as WeightValueEntry)
                  : { weight: String(entry) };
              return [
                attributeId,
                {
                  ...baseEntry,
                  percentage:
                    allAttributeGlobalPercentages[datasetId]?.[attributeId] ||
                    "0.00"
                } as WeightValueEntry
              ];
            })
        );

        return [
          datasetId,
          {
            weight: dataset.weight,
            attributes: combinedAttributes
          }
        ];
      })
    );
  };

  const hasWeightValuesChanged = useDeepCompareMemo(
    () =>
      areObjectsDifferentDeeply(localWeightValues, weightValues) &&
      Object.keys(localWeightValues).length ===
        Object.keys(weightValues).length,
    [localWeightValues, weightValues]
  );

  const isAllAttributeWeightZero = useDeepCompareMemo(
    () =>
      !filteredVisualizationOrder.some((datasetId) =>
        Object.values(localWeightValues[datasetId]?.attributes || {}).some(
          hasNonZeroValue
        )
      ),
    [localWeightValues, filteredVisualizationOrder]
  );

  const isAllDatasetWeightZero = useDeepCompareMemo(
    () =>
      !filteredVisualizationOrder.some((datasetId) =>
        hasNonZeroValue(localWeightValues[datasetId]?.weight)
      ),
    [localWeightValues, filteredVisualizationOrder]
  );

  const handleSaveSetting = () => {
    const combinedWeightValues = getCombinedWeightValues();
    dispatch(saveWeightValues({ weightValues: combinedWeightValues }));
    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        scoringOption: selectedScoringOption
      })
    );
    onClose();
  };

  const resetAllWeights = () => {
    setLocalWeightValues(initializeDatasetWeights(weightValues));
    setLocalWeightValues(weightValues);
  };

  useEffect(() => {
    setLocalWeightValues(initializeDatasetWeights(weightValues));
  }, [weightValues]);

  return (
    <div
      className={twMerge(
        "z-[99] font-sans border-gainsboro bg-white rounded-lg w-[338px] max-h-[90vh]",
        "flex flex-col pt-4 absolute left-[370px] top-[30px] text-xs"
      )}
    >
      {/* Header */}
      <div className="flex flex-row items-start justify-between px-4">
        <div className="flex flex-row items-center gap-2">
          <h2 className="text-txtHeading font-bold text-[20px]">
            Setting Data Weight
          </h2>
        </div>
        <button
          className="p-0 bg-transparent border-0"
          onClick={onClose}
          type="button"
          aria-label="Close"
        >
          <CloseIcon className="fill-dim-gray" />
        </button>
      </div>

      <div className="px-4 py-2">
        <Alert className="border border-neutral-200 bg-[#FAFAFA]">
          <AlertTitle className="font-semibold text-sm text-txtBody mb-0.5">
            Negative impact
          </AlertTitle>
          <AlertDescription className="text-xs text-txtBody">
            Check this option to treat lower data values as better scores during
            calculation.
          </AlertDescription>
        </Alert>
      </div>

      {/* Dataset Attributes Weight Section */}
      <div className="flex flex-col gap-2 mt-3 overflow-y-auto divide-y-8 custom-scrollbar divide-white-smoke scrollbar-overlay">
        <Accordion
          type="multiple"
          defaultValue={filteredVisualizationOrder}
          className="flex flex-col gap-4"
        >
          {filteredVisualizationOrder.map((datasetId: string) => (
            <DatasetAttributeItem
              key={datasetId}
              dataset={availableDatasets[datasetId]}
              datasetAttributesWeights={Object.fromEntries(
                Object.entries(
                  localWeightValues[datasetId]?.attributes || {}
                ).filter(([, value]) => value !== undefined)
              )}
              datasetWeightValue={localWeightValues[datasetId]?.weight}
              datasetWeightPercentage={datasetWeightPercentages[datasetId]}
              attributeGlobalPercentages={
                allAttributeGlobalPercentages[datasetId] || {}
              }
              setLocalWeightValues={setLocalWeightValues}
              isAllAttributeWeightZero={isAllAttributeWeightZero}
              isAllDatasetWeightZero={isAllDatasetWeightZero}
              selectedScoringOption={selectedScoringOption}
            />
          ))}
        </Accordion>

        {/* Scoring Option Section */}
        <ScoringOptionsSection
          setSelectedScoringOption={setSelectedScoringOption}
          selectedScoringOption={selectedScoringOption}
        />
      </div>
      {/* Data Weight Summary Section */}
      <WeightSummarySection
        availableDatasets={availableDatasets}
        totalAttributes={totalAttributes}
        hasWeightValuesChanged={hasWeightValuesChanged}
        selectedScoringOption={selectedScoringOption}
        isAllAttributeWeightZero={isAllAttributeWeightZero}
        isAllDatasetWeightZero={isAllDatasetWeightZero}
        resetAllWeights={resetAllWeights}
        handleSaveSetting={handleSaveSetting}
      />
    </div>
  );
};

export default DataWeightSettingsPopupV2;
